import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, Lock, Play } from 'lucide-react';
import { Badge, Button, Card, CardContent, Checkbox, Progress } from '@mlm/ui';
import {
  listMyCourses,
  listMyCreatorSubscriptions,
  openBillingPortal,
  productImageUrl,
  setCreatorAutoRenew,
  type MyCourse,
  type MyCreatorSubscription,
} from '../lib/api';
import { useCartStore } from '../storefront/lib/cart-store';
import { peso } from '../storefront/lib/format';
import { CourseTile, CourseTileSkeleton } from '../storefront/components/course-tile';
import { useCourseCatalog, useWishlist } from '../storefront/lib/hooks';
import { fmtDate } from '../lib/format';

/** The three ways a learner owns a course, as headed groups. */
const COURSE_GROUPS: { access: string; title: string }[] = [
  { access: 'free', title: 'Free courses' },
  { access: 'purchase', title: 'Paid courses' },
  { access: 'subscriber', title: 'Subscription courses' },
];

/** Why a renewal was stopped or turned off, worded for the member. */
const AUTO_RENEW_STOP_COPY: Record<string, string> = {
  insufficient_funds: 'It stopped after a week of tries because your wallet was short.',
  plan_pending: 'It stopped while the instructor\u2019s new price waited for approval.',
  renewal_failed: 'It stopped after repeated errors on our side.',
  creator_unavailable: 'The instructor no longer offers a subscription.',
  plan_unavailable: 'The instructor no longer offers this plan.',
  price_increased: 'The price went up, so it stopped. Turn it back on to accept the new price.',
  slot_inactive: 'The wallet it paid from is no longer active.',
  eligibility_lost: 'Your account no longer qualifies for this subscription.',
  lapsed: 'It stopped because the subscription ended more than a week ago.',
  account_inactive: 'It stopped while your account was inactive.',
  refunded: 'It was turned off when the purchase was refunded.',
  revoked_by_admin: 'It was turned off by support.',
  stopped_by_admin: 'It was turned off by support.',
  // Card subscriptions (Stripe)
  card_cancel_scheduled: 'Card renewal is off. You keep access until the date shown.',
  card_canceled: 'You cancelled the card subscription.',
  card_payment_failed: 'It stopped because your card couldn\u2019t be charged after several tries.',
  card_payment_disputed: 'It stopped because the card payment was disputed.',
  card_canceled_in_stripe: 'The card subscription was cancelled.',
  order_unavailable: 'The card subscription was cancelled because its order was no longer open.',
};

/** "each month" / "every 6 months" / "each year", for a card's billing line. */
const CARD_CADENCE: Record<string, string> = {
  monthly: 'each month',
  semi_annual: 'every 6 months',
  annual: 'each year',
};

/**
 * Phase 10 — the member's creator subscriptions.
 *
 * A LAPSED subscription is listed too, with Renew now, because the courses it
 * unlocked simply stop opening otherwise and nothing on the page would say
 * why. Renders nothing when the member has never subscribed.
 *
 * Auto-renew: the switch re-consents at today's price from an active slot's
 * wallet (or turns it off; paid time is never touched). Renew now puts the
 * same plan in the cart and opens checkout, so any payment method works.
 *
 * A CARD subscription (Stripe) renews by itself: the wallet switch and Renew
 * now step aside, and Manage billing opens the Stripe Customer Portal (update
 * the card, cancel or resume), with Pay now while a charge is outstanding.
 */
function MyCreatorSubscriptions() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const addOnly = useCartStore((s) => s.addOnly);
  const { data: subs = [] } = useQuery({
    queryKey: ['my-creator-subscriptions'],
    queryFn: listMyCreatorSubscriptions,
  });
  const toggle = useMutation({
    meta: { errorMessage: "Couldn't change automatic renewal. Please try again." },
    mutationFn: (v: { creatorUserId: string; enabled: boolean }) =>
      setCreatorAutoRenew(v.creatorUserId, v.enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-creator-subscriptions'] }),
  });
  // Card subscriptions are managed on Stripe's own page; its changes come
  // back to us as webhooks, so this page only has to send the member there.
  const billing = useMutation({
    meta: { errorMessage: "Couldn't open billing. Please try again." },
    mutationFn: openBillingPortal,
    onSuccess: ({ url }) => window.location.assign(url),
  });
  if (subs.length === 0) return null;

  function renewNow(s: MyCreatorSubscription) {
    const plan = s.renewPlan;
    if (!plan) return;
    // Tagged exactly as the course page tags a plan line: a third-party
    // creator's plan becomes that creator's sub-order, a house plan a house line.
    const partner = !s.creatorIsHouse;
    addOnly({
      productId: plan.productId,
      name: `${s.creatorName} subscription \u2014 ${plan.label}`,
      price: Number(plan.price),
      was: Number(plan.price),
      priceGc: 0,
      priceCoin: 0,
      image: null,
      variation: '',
      pv: 0,
      isVirtual: true,
      isCourse: false,
      sellerId: partner ? s.creatorUserId : '',
      sellerName: partner ? s.creatorName : '',
      ...(partner ? { sellerKind: 'creator' as const } : {}),
      planPeriod: plan.period,
      planCreatorId: s.creatorUserId,
      qty: 1,
    });
    navigate('/checkout');
  }

  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">Subscriptions</h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {subs.map((s) => {
          const renewal = s.autoRenew;
          const on = renewal?.status === 'active' || renewal?.status === 'past_due';
          const stopNote =
            renewal && !on && renewal.stoppedReason
              ? (AUTO_RENEW_STOP_COPY[renewal.stoppedReason] ?? null)
              : null;
          const busy = toggle.isPending && toggle.variables?.creatorUserId === s.creatorUserId;
          // A card subscription renews by itself; the wallet switch steps aside.
          const card = s.card ?? null;
          const cardOn = !!card && on;
          return (
            <Card key={s.creatorUserId}>
              <CardContent className="space-y-2 p-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{s.creatorName}</span>
                  <span className="text-xs text-muted-foreground">{s.periodLabel}</span>
                  {!s.active ? (
                    <Badge variant="muted">Lapsed {fmtDate(s.expiresAt)}</Badge>
                  ) : renewal?.status === 'past_due' ? (
                    <Badge variant="warning">
                      {cardOn ? 'Card payment failed' : 'Renewal retrying'}
                    </Badge>
                  ) : cardOn ? (
                    <Badge variant="success">
                      Renews by card {fmtDate(card.renewsAt ?? s.expiresAt)}
                    </Badge>
                  ) : on ? (
                    <Badge variant="success">Renews {fmtDate(s.expiresAt)}</Badge>
                  ) : (
                    <Badge variant="outline">Ends {fmtDate(s.expiresAt)}</Badge>
                  )}
                </div>
                {cardOn && renewal?.status === 'active' && (
                  <p className="text-xs text-muted-foreground">
                    {peso(card.price)} is charged to your card{' '}
                    {CARD_CADENCE[renewal.period] ?? 'each period'}. Access is paid through{' '}
                    {fmtDate(s.expiresAt)}. Cancel anytime in Manage billing; you keep access until
                    the end of what you paid for.
                  </p>
                )}
                {renewal?.status === 'past_due' &&
                  (cardOn ? (
                    <p className="text-xs text-muted-foreground">
                      Your card payment didn&rsquo;t go through. Stripe tries again automatically;
                      tap Pay now, or update your card in Manage billing, to keep learning.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Your wallet was short at renewal. We try again every day for a week; top up to
                      keep learning.
                    </p>
                  ))}
                {stopNote && <p className="text-xs text-muted-foreground">{stopNote}</p>}
                <div className="flex flex-wrap items-center gap-3">
                  {cardOn && card.payUrl && (
                    <Button size="sm" onClick={() => window.location.assign(card.payUrl!)}>
                      Pay now
                    </Button>
                  )}
                  {card?.manageable && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={billing.isPending}
                      onClick={() => billing.mutate()}
                    >
                      Manage billing
                    </Button>
                  )}
                  {s.autoRenewBlocked !== 'unavailable' && s.autoRenewBlocked !== 'card_active' && (
                    <label className="flex items-center gap-2 text-xs">
                      <Checkbox
                        checked={on}
                        disabled={busy || (!on && s.autoRenewBlocked !== null)}
                        onCheckedChange={(v) =>
                          toggle.mutate({ creatorUserId: s.creatorUserId, enabled: v === true })
                        }
                        aria-label={`Renew ${s.creatorName} automatically from my wallet`}
                      />
                      {on && renewal
                        ? `Auto-renew from my wallet at ${peso(renewal.consentedPrice)}`
                        : 'Auto-renew from my wallet'}
                    </label>
                  )}
                  {s.renewPlan && !cardOn && (
                    <Button
                      size="sm"
                      variant={s.active ? 'outline' : 'default'}
                      className="ml-auto"
                      onClick={() => renewNow(s)}
                    >
                      Renew now
                    </Button>
                  )}
                </div>
                {!on && s.autoRenewBlocked === 'requires_slot' && (
                  <p className="text-xs text-muted-foreground">Activate a slot to auto-renew.</p>
                )}
                {!on && s.autoRenewBlocked === 'plan_unavailable' && (
                  <p className="text-xs text-muted-foreground">
                    This plan is not on sale right now.{' '}
                    <Link to="/courses?price=subscriber" className="underline">
                      Browse subscriptions
                    </Link>
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}

/**
 * My Courses — every enrollment (revoked AND expired ones render as a LOCKED
 * card, never a 403 page) with progress, a Resume pointer and a completion
 * badge.
 */
export function MyCoursesPage() {
  const q = useInfiniteQuery({
    queryKey: ['my-courses'],
    queryFn: ({ pageParam }) => listMyCourses(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">My Courses</h1>
        <Button asChild variant="outline" size="sm">
          <Link to="/courses">Browse catalog</Link>
        </Button>
      </div>

      <MyCreatorSubscriptions />

      <SavedCourses />

      {q.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-lg border">
              <div className="aspect-video rounded-t-lg bg-muted" />
              <div className="space-y-2 p-4">
                <div className="h-4 w-3/4 rounded bg-muted" />
                <div className="h-3 w-1/2 rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            You haven't enrolled in any course yet.{' '}
            <Link to="/courses" className="underline">
              Pick your first course
            </Link>
            .
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {COURSE_GROUPS.map((group) => {
            const groupItems = items.filter((c) => c.accessLevel === group.access);
            if (groupItems.length === 0) return null;
            return (
              <section key={group.access}>
                {/* Grouped, not sorted: how a learner GOT a course decides what
                    they can do about losing it. A lapsed subscription and a
                    refunded purchase both read as "locked" on a flat list. */}
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.title}
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {groupItems.map((c) => (
                    <EnrollmentCard key={c.courseId} c={c} />
                  ))}
                </div>
              </section>
            );
          })}
          {/* A row whose access level is none of the three (an admin grant on a
              course whose level changed later) still has to appear somewhere. */}
          {items.some((c) => !COURSE_GROUPS.some((g) => g.access === c.accessLevel)) && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Other
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items
                  .filter((c) => !COURSE_GROUPS.some((g) => g.access === c.accessLevel))
                  .map((c) => (
                    <EnrollmentCard key={c.courseId} c={c} />
                  ))}
              </div>
            </section>
          )}
        </div>
      )}
      {q.hasNextPage && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
          >
            Load more
          </Button>
        </div>
      )}
      {/* A link, not the list: certificates have their own page (and nav entry)
          so an empty one can still say where they will appear. */}
      <p className="text-sm text-muted-foreground">
        Looking for a certificate?{' '}
        <Link to="/my-certificates" className="text-primary hover:underline">
          My certificates
        </Link>
      </p>
    </div>
  );
}

/**
 * Courses the member hearted but hasn't enrolled in — the listing surface the
 * wishlist needed to stop being a write-only feature. Saved NON-course products
 * are deliberately not shown: there is no member-side page for them yet, and a
 * row that leads nowhere is worse than an omission.
 */
function SavedCourses() {
  const { ids, isLoading: wishLoading } = useWishlist();
  const productIds = [...ids];
  const { tiles, isLoading } = useCourseCatalog({
    productIds,
    limit: 24,
    enabled: productIds.length > 0,
  });

  if (!wishLoading && productIds.length === 0) {
    return (
      <section>
        <h2 className="text-lg font-semibold">Saved courses</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Tap the heart on any course to save it here.
        </p>
      </section>
    );
  }

  return (
    <section className="omni-store">
      <h2 className="text-lg font-semibold">Saved courses</h2>
      <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
        {wishLoading || isLoading
          ? Array.from({ length: 4 }).map((_, i) => <CourseTileSkeleton key={i} />)
          : tiles.map((t, i) => (
              <CourseTile key={t.product.id} product={t.product} course={t.course} index={i} />
            ))}
      </div>
      {!wishLoading && !isLoading && tiles.length === 0 && (
        <p className="mt-1 text-sm text-muted-foreground">
          The courses you saved are no longer in the catalog.
        </p>
      )}
    </section>
  );
}

/**
 * One enrollment card. Extracted so the list can be GROUPED by how the learner
 * came to own the course without duplicating 90 lines per group.
 */
function EnrollmentCard({ c }: { c: MyCourse }) {
  // Timed access runs out on the client's clock between refetches, so
  // the row can still arrive un-revoked with a date already past.
  const expired = !!c.expiresAt && new Date(c.expiresAt).getTime() <= Date.now();
  const locked = !!c.revokedAt || expired;
  return (
    <Card className={locked ? 'opacity-70' : ''}>
      <Link to={locked ? `/course/${c.productId}` : `/learn/${c.courseId}`}>
        <div className="aspect-video overflow-hidden rounded-t-lg bg-muted">
          {c.imageCount > 0 && (
            <img
              src={productImageUrl(c.productId, 0)}
              alt=""
              className="h-full w-full object-cover"
            />
          )}
        </div>
      </Link>
      <CardContent className="space-y-3 p-4">
        <div>
          <Link
            to={locked ? `/course/${c.productId}` : `/learn/${c.courseId}`}
            className="line-clamp-2 font-semibold hover:underline"
          >
            {c.productName}
          </Link>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {c.lessonCount} lesson{c.lessonCount === 1 ? '' : 's'}
            {c.resumeLessonTitle && !c.completed && !locked
              ? ` · Next: ${c.resumeLessonTitle}`
              : ''}
          </div>
        </div>
        {locked ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            {expired && !c.revokedAt
              ? `Access expired ${fmtDate(c.expiresAt)}`
              : 'Access ended (refunded)'}
          </div>
        ) : (
          <>
            <div className="space-y-1">
              <Progress value={c.progressPct} />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{c.progressPct}% complete</span>
                <span>
                  {c.completedCount}/{c.lessonCount}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Button asChild size="sm">
                <Link
                  to={`/learn/${c.courseId}${c.resumeLessonId ? `?lesson=${c.resumeLessonId}` : ''}`}
                >
                  <Play className="mr-1 h-3.5 w-3.5" />
                  {c.completedCount === 0 ? 'Start' : c.completed ? 'Review' : 'Resume'}
                </Link>
              </Button>
              {c.certificateEligible && c.hasCertificateTemplate ? (
                // The badge is the only path from here to an earned
                // certificate, so it has to be a link. `?cert=1` is
                // the same deep link the certificate bell uses.
                <Link
                  to={`/learn/${c.courseId}?cert=1`}
                  className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Award className="h-4 w-4" /> View certificate
                </Link>
              ) : c.certificateEligible ? (
                <span className="flex items-center gap-1 text-xs font-medium text-primary">
                  <Award className="h-4 w-4" /> Completed
                </span>
              ) : c.completed ? (
                // Lessons done but a test still stands between the
                // learner and the certificate.
                <Link
                  to={`/learn/${c.courseId}`}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Test remaining →
                </Link>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
